/* The builder's day MODAL — its BOX and its scrim, and nothing else.
 *
 * `Builder planu - modal - progresja` draws it (`panelSt`, `panelScrim`, ~lines 1082–1091 of the
 * export): the panel's own chrome in a card 80% of the screen wide, 24px clear of the top and
 * bottom, centred, radius 18, on `--bg-sunken` behind a blurred scrim.
 *
 * ── What this file does NOT hold ────────────────────────────────────────────────────────────
 * The form, the drawer, the drawer's scrim. It held byte-identical copies of all three until 13
 * September 2026, under a header claiming the two sheets shared nothing — while `.form`,
 * `.libraryScrim`, `.drawer` and `.drawerClosed` were the same declarations twice. There is one
 * host component now, so they are declared once in `DayPanelHost.module.css` and this sheet says
 * only what the box changes: its geometry, its entrance, its scrim, and the drawer scrim's ink.
 *
 * The GEOMETRY is genuinely a different drawing and stays here. The panel is a full-height 574px
 * column pinned to the right edge, sliding in horizontally under a horizontal shadow, with no scrim
 * at all; this is a centred card rising and scaling in under a downward shadow.
 *
 * `line-height: var(--lh-auto)` is carried across rather than inherited, for the reason the host
 * sheet's header gives: `body` sets `--lh-relaxed` (1.5) app-wide, and forty-odd micro-labels in
 * this dock inheriting prose leading pushed the library rail beside them 3.5px down.
 */

/* ── The box ─────────────────────────────────────────────────────────────────────────────── */

/* The mock positions the modal `absolute` inside its own frame; this app portals the popup into
 * `<body>`, where `absolute` would resolve against the initial containing block and scroll away
 * with the page. `fixed` is the same drawing with the same edges.
 *
 * ── The centring is INSETS, and it may never be a transform ─────────────────────────────────
 * The mock's 80% width, centred, was drawn as `left: 50%` plus `width: 80%` plus a
 * `translateX(-50%)` — and that is the one way to write it that this component cannot use. The two
 * boxes are ONE element, class-swapped (see `DayPanelHost`), and a class swap changes computed
 * values on an element that is already transitioning `transform`: the centring translate ANIMATED
 * on every press of the toggle. Measured at 1440x900 on 13 September 2026 — maximise put the box at
 * `left 720 / right 1872`, 432px past the viewport edge, and slid it home over ~390ms; restore left
 * the panel 287px adrift of the right edge with the week showing through. A fixed interactive
 * surface outside the viewport, with no scroll path, for a sixth of a second, on every switch.
 *
 * `inset-inline: 10%` is the same drawing with no transform in it, so the box the switch changes is
 * a box that changes IN PLACE. `transform` here belongs to the entrance and the exit alone. */
.modal {
  position: fixed;
  z-index: 20;
  display: flex;
  flex-direction: column;
  inset-block: var(--space-6);
  inset-inline: 10%;
  overflow: hidden;
  background: var(--bg-sunken);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-lg);

  /* Downward and neutral, unlike the panel's horizontal cast: this card floats centred over the
   * week rather than arriving from an edge, so it is lit from above. */
  box-shadow: var(--shadow-day-modal);

  /* See the header. */
  line-height: var(--lh-auto);

  /* The drawer scrim's ink in THIS box, read by `.libraryScrim` in the host sheet. The drawer covers
   * 320px of a box more than a thousand wide, so what it does not cover is a whole day standing
   * fully lit and taking no press — every other overlay in this app puts what it covers visibly
   * behind a layer, and so does this one. The panel's 254px strip stays transparent. */
  --scrim-library: var(--scrim);

  /* The mock's own pair, and it runs them at two lengths on purpose: the box is fully opaque before
   * it has finished settling, so the rise reads as a settle rather than as a fade. Transform and
   * opacity only — nothing behind the modal is laid out again as it arrives. `prefers-reduced-motion`
   * collapses both, once, in `tokens/base.css`. */
  transition:
    transform var(--dur-day-modal) var(--ease-fp),
    opacity var(--dur-day-modal-fade) var(--ease-default);
}

/* The mock's closed state, reached through Base UI's own transition hooks rather than through a
 * flag: the popup is mounted only while the dock is open, and these two attributes are what let it
 * rise in on arrival and sink back out before it unmounts. It carries no centring term — the box is
 * centred by its insets, exactly so that this is the ONLY transform the modal ever holds and a host
 * switch therefore animates nothing. See the block above.
 *
 * `pointer-events: none` is the panel's rule and was missing here until 13 September 2026: for the
 * 280ms the modal spends sinking out, a card pressed on the week behind it was hitting the leaving
 * box instead — the press that closed the day swallowed the press that opened the next one. */
.modal[data-starting-style],
.modal[data-ending-style] {
  opacity: 0;
  transform: translateY(14px) scale(0.985);
  pointer-events: none;
}

/* ── The scrim ───────────────────────────────────────────────────────────────────────────── */

/* Its own colour per theme rather than the app's `--scrim`, because the mock states both: the modal
 * covers most of the screen and the little of the week left visible is dimmed harder than a dialog
 * dims a page.
 *
 * The BLUR is the mock's and it stays. Owner, 13 September 2026: the "no glass" ban is about a
 * coloured translucent surface pretending to be frosted material, not about a neutral blur under a
 * dimming layer. See CLAUDE.md and `--blur-scrim`. */
.scrim {
  position: fixed;
  inset: var(--space-0);
  z-index: 19;
  background: var(--scrim-day-modal);
  backdrop-filter: var(--blur-scrim);
  transition-duration: var(--dur-day-modal-scrim);
  transition-property: opacity;
  transition-timing-function: var(--ease-default);
}

/* Transparent AND out of the way — the scrim outlives the modal's own exit by the length of its
 * fade, and an invisible full-screen layer still takes every press aimed at the week under it. */
.scrim[data-starting-style],
.scrim[data-ending-style] {
  opacity: 0;
  pointer-events: none;
}
